import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import * as Sentry from "@sentry/react";
import Cookies from "js-cookie";

import { PROD_API_BASE_URL, SHOW_API_SELECTOR } from "constants/constants";
import * as LOCAL_STORAGE from "utils/localStorage";
import { getClientId } from "utils/localStorage";

import { refreshAuthToken, setTokens } from "./authentication";

class APIError extends Error {
  constructor(name: string, message: string) {
    super(message);
    this.name = "API Error - " + name;
  }
}

const getBaseUrl = () => {
  if (import.meta.env.VITEST) {
    return "https://localhost:5000";
  }
  if (SHOW_API_SELECTOR) {
    return LOCAL_STORAGE.getCustomAPIEndPoint() || PROD_API_BASE_URL;
  }

  return PROD_API_BASE_URL;
};

const returnToLogin = () => {
  sessionStorage.setItem("returnURL", globalThis.location.pathname);

  globalThis.location.href = "/auth/login";
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: "/",
  validateStatus: (response) =>
    // Do not treat 404 not found as an error, as we check for the data in the component
    response.status < 300 || response.status === 404,
  async prepareHeaders(headers) {
    headers.set("Accept", "application/json");

    const token = Cookies.get("access_token");
    const refresh_token = Cookies.get("refresh_token");
    const client_id = getClientId();

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    } else if (refresh_token && client_id) {
      const response = await refreshAuthToken({ refresh_token, client_id });

      setTokens(response);

      headers.set("Authorization", `Bearer ${response.access_token}`);
    }

    return headers;
  },
  paramsSerializer(params) {
    const search = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (Array.isArray(value)) {
        value.forEach((v) => search.append(key, String(v)));
      } else if (value !== undefined && value !== null) {
        search.set(key, String(value));
      }
    }
    return search.toString();
  },
});

const dynamicBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const baseUrl = getBaseUrl();

  const adjustedArgs =
    typeof args === "string"
      ? { url: new URL("api/v1" + args, baseUrl).toString() }
      : { ...args, url: new URL("api/v1" + args.url, baseUrl).toString() };

  const result = await rawBaseQuery(adjustedArgs, api, extraOptions);

  const status = result.error?.status;
  const isUnauthorized =
    status === 401 ||
    (status === "PARSING_ERROR" && result.error?.originalStatus === 401);

  if (isUnauthorized) {
    returnToLogin();
  } else if (result.error && status !== "FETCH_ERROR") {
    const query = typeof args === "string" ? args : args.url;
    const statusString = status?.toString() ?? "Unknown";

    Sentry.captureException(new APIError(api.endpoint, statusString), {
      fingerprint: ["base-api-error", api.endpoint, statusString],
      extra: {
        args,
        query,
        adjustedArgs,
        error: result.error,
        apiState: api.getState?.(),
      },
      level: "error",
    });
  }

  return result;
};

const dynamicBaseQueryAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const baseUrl = getBaseUrl();

  const adjustedArgs =
    typeof args === "string"
      ? baseUrl
      : { ...args, url: new URL("api/auth/v1" + args.url, baseUrl).toString() };

  const result = await rawBaseQuery(adjustedArgs, api, extraOptions);

  const status = result.error?.status;
  const isUnauthorized =
    status === 401 ||
    (status === "PARSING_ERROR" && result.error?.originalStatus === 401);

  if (isUnauthorized && !globalThis.location.pathname.startsWith("/auth")) {
    returnToLogin();
  }

  return result;
};

export const baseApi = createApi({
  baseQuery: dynamicBaseQuery,
  reducerPath: "api",
  endpoints: () => ({}),
});

export const baseApiAuth = createApi({
  baseQuery: dynamicBaseQueryAuth,
  reducerPath: "api-auth",
  endpoints: () => ({}),
});
